import { useEffect, useMemo, useState } from "react";
import {
    Building2, Calendar, CheckCircle2, Clock, Download, Filter, Loader2,
    Mail, MessageSquarePlus, Phone, Search, Ban, User, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STATUS_BADGE = {
    nuevo: "badge-gold",
    contactado: "badge-exito",
    descartado: "badge-error",
};
const ROLES_FILTER = ["Inversionista", "Fondo", "Lender", "Fiduciario", "Otro"];

function fmt(iso) {
    if (!iso) return "—";
    return String(iso).slice(0, 16).replace("T", " ");
}

function LeadDetailDialog({ lead, onChanged, children }) {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [notaTexto, setNotaTexto] = useState("");
    const [nuevoStatus, setNuevoStatus] = useState("");
    const [motivoDescarte, setMotivoDescarte] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        if (!lead?.id) return;
        const { data } = await api.get(`/admin/leads/${lead.id}`);
        setDetail(data);
        setNuevoStatus(data.status);
    };
    useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, lead?.id]);

    const cambiarStatus = async () => {
        if (nuevoStatus === "descartado" && !motivoDescarte) {
            toast.error("Motivo requerido para descartar"); return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/leads/${lead.id}/status`, {
                status: nuevoStatus, motivo: motivoDescarte || null,
            });
            toast.success(`Status → ${nuevoStatus}`);
            await load();
            onChanged?.();
        } catch (err) {
            toast.error("Error cambiando status", {
                description: err?.response?.data?.detail,
            });
        } finally { setSaving(false); }
    };

    const agregarNota = async () => {
        if (!notaTexto || notaTexto.length < 1) return;
        setSaving(true);
        try {
            await api.post(`/admin/leads/${lead.id}/nota`, { texto: notaTexto });
            toast.success("Nota agregada");
            setNotaTexto("");
            await load();
            onChanged?.();
        } catch (err) {
            toast.error("Error agregando nota");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{lead.nombre} · {lead.empresa}</DialogTitle>
                </DialogHeader>
                {!detail ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm py-6">
                        <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Info básica */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rol</div>
                                <div>{detail.rol}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fecha</div>
                                <div className="tabular">{fmt(detail.created_at)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</div>
                                <a href={`mailto:${detail.email}`} className="text-[color:var(--brand-dark-gold)]">
                                    {detail.email}
                                </a>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Teléfono</div>
                                <a href={`tel:${detail.telefono}`}>{detail.telefono}</a>
                            </div>
                            {detail.tipos_fideicomiso?.length > 0 && (
                                <div className="col-span-2">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipos de interés</div>
                                    <div className="flex gap-1 mt-1">
                                        {detail.tipos_fideicomiso.map((t) => (
                                            <span key={t} className="badge-gold text-[10px]">{t}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="col-span-2">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Mensaje</div>
                                <div className="mt-1 p-3 bg-muted rounded-sm text-sm leading-relaxed">
                                    {detail.mensaje}
                                </div>
                            </div>
                        </div>

                        {/* Cambiar status */}
                        <div className="border-t border-border pt-4">
                            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                                Cambiar status
                            </div>
                            <div className="flex gap-2 items-end">
                                <Select value={nuevoStatus} onValueChange={setNuevoStatus}>
                                    <SelectTrigger className="flex-1" data-testid={`lead-status-select-${lead.id}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="nuevo">Nuevo</SelectItem>
                                        <SelectItem value="contactado">Contactado</SelectItem>
                                        <SelectItem value="descartado">Descartado</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button onClick={cambiarStatus} disabled={saving || nuevoStatus === detail.status}
                                    data-testid={`lead-status-save-${lead.id}`}>
                                    Guardar
                                </Button>
                            </div>
                            {nuevoStatus === "descartado" && (
                                <Input value={motivoDescarte}
                                    onChange={(e) => setMotivoDescarte(e.target.value)}
                                    placeholder="Motivo de descarte..."
                                    className="mt-2" />
                            )}
                        </div>

                        {/* Notas / timeline */}
                        <div className="border-t border-border pt-4">
                            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                                Notas de contacto ({detail.notas?.length || 0})
                            </div>
                            {detail.notas?.length > 0 && (
                                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                                    {detail.notas.map((n) => (
                                        <div key={n.id} className="p-3 bg-muted/60 rounded-sm text-sm">
                                            <div className="text-[10px] text-muted-foreground tabular">
                                                {fmt(n.created_at)} · {n.created_by}
                                            </div>
                                            <div className="mt-1">{n.texto}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <Input value={notaTexto} onChange={(e) => setNotaTexto(e.target.value)}
                                    placeholder="Nueva nota de contacto..." />
                                <Button variant="outline" onClick={agregarNota} disabled={saving || !notaTexto}
                                    data-testid={`lead-nota-add-${lead.id}`}>
                                    <MessageSquarePlus className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [rolFilter, setRolFilter] = useState("");
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (rolFilter) params.set("rol", rolFilter);
            if (q) params.set("q", q);
            const { data } = await api.get(`/admin/leads?${params}`);
            setLeads(data);
        } catch (err) {
            toast.error("Error cargando leads", {
                description: err?.response?.data?.detail,
            });
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, rolFilter]);

    const exportCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            const r = await api.get(`/admin/leads/export.csv?${params}`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "leads.csv"; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Error exportando CSV"); }
    };

    const stats = useMemo(() => {
        const s = { nuevo: 0, contactado: 0, descartado: 0 };
        leads.forEach((l) => { s[l.status] = (s[l.status] || 0) + 1; });
        return s;
    }, [leads]);

    return (
        <div className="p-8 lg:p-10 max-w-[1500px] space-y-6" data-testid="leads-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Fase 11 · Landing
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Mail className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Leads
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Solicitudes de acceso recibidas por el formulario público de la landing.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={load}>
                        <RefreshCw className="h-4 w-4 mr-2" /> Actualizar
                    </Button>
                    <Button variant="outline" onClick={exportCsv} data-testid="leads-export-csv">
                        <Download className="h-4 w-4 mr-2" /> Export CSV
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-3 gap-4">
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Nuevos</div>
                    <div className="kpi-value mt-2 text-[color:var(--brand-dark-gold)]">{stats.nuevo}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contactados</div>
                    <div className="kpi-value mt-2 text-[color:var(--brand-exito)]">{stats.contactado}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Descartados</div>
                    <div className="kpi-value mt-2 text-muted-foreground">{stats.descartado}</div>
                </div>
            </div>

            {/* Filtros */}
            <div className="flex gap-3 flex-wrap items-center rounded-md border border-border bg-card p-3">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40" data-testid="leads-filter-status">
                        <SelectValue placeholder="Todos los status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los status</SelectItem>
                        <SelectItem value="nuevo">Nuevo</SelectItem>
                        <SelectItem value="contactado">Contactado</SelectItem>
                        <SelectItem value="descartado">Descartado</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={rolFilter || "all"} onValueChange={(v) => setRolFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40" data-testid="leads-filter-rol">
                        <SelectValue placeholder="Todos los roles" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los roles</SelectItem>
                        {ROLES_FILTER.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="h-3.5 w-3.5 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Buscar nombre, empresa o email…"
                        className="max-w-xs" data-testid="leads-search" />
                    <Button variant="ghost" size="sm" onClick={load}>Buscar</Button>
                </div>
            </div>

            {/* Tabla */}
            <div className="rounded-md border border-border bg-card overflow-hidden">
                {loading ? (
                    <div className="p-10 flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Cargando leads…
                    </div>
                ) : (
                    <table className="trf-table">
                        <thead><tr>
                            <th>Fecha</th>
                            <th>Nombre · Empresa</th>
                            <th>Rol</th>
                            <th>Contacto</th>
                            <th>Tipos</th>
                            <th>Status</th>
                            <th></th>
                        </tr></thead>
                        <tbody>
                            {leads.map((l) => (
                                <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                                    <td className="tabular text-[11px] text-muted-foreground">
                                        {fmt(l.created_at)}
                                    </td>
                                    <td>
                                        <div className="font-medium">{l.nombre}</div>
                                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                                            <Building2 className="h-3 w-3" /> {l.empresa}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge-gold text-[10px]">{l.rol}</span>
                                    </td>
                                    <td className="text-[11px]">
                                        <div className="flex items-center gap-1">
                                            <Mail className="h-3 w-3 text-muted-foreground" />
                                            <span className="truncate max-w-[180px]">{l.email}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                            <Phone className="h-3 w-3" /> {l.telefono}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-wrap gap-1">
                                            {(l.tipos_fideicomiso || []).map((t) => (
                                                <span key={t} className="badge-gold text-[9px]">{t}</span>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={STATUS_BADGE[l.status] || "badge-gold"}
                                            data-testid={`lead-status-${l.id}`}>
                                            {l.status}
                                        </span>
                                    </td>
                                    <td>
                                        <LeadDetailDialog lead={l} onChanged={load}>
                                            <Button variant="ghost" size="sm"
                                                data-testid={`lead-view-${l.id}`}>
                                                Ver
                                            </Button>
                                        </LeadDetailDialog>
                                    </td>
                                </tr>
                            ))}
                            {leads.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center py-12 text-muted-foreground text-sm">
                                        Sin leads con estos filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
